/**
 * Arousal Mechanics Browser Page
 * 
 * Refactored to match standard premium UI with infinite scroll and table/card views.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    Search, AlertTriangle, Activity
} from 'lucide-react';
import { useDevMode } from '../contexts/DevModeContext';
import { useCollection } from '../contexts/CollectionContext';
import EditableArousalMechanicRow from '../components/EditableArousalMechanicRow';
import DatabaseHero from '../components/DatabaseHero';
import Spinner from '../components/Spinner';

function ArousalMechanicsPage() {
    const { isDevMode } = useDevMode();
    const {
        mechanics, loadMechanics, isMechanicsLoaded, mechanicsLoading, mechanicsError,
        totalMechanics
    } = useCollection();

    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    // Initial Load
    useEffect(() => {
        if (!isMechanicsLoaded) {
            loadMechanics({}, 1);
        }
    }, [isMechanicsLoaded, loadMechanics]);

    // Handle Search/Filter
    const handleSearch = useCallback((newSearch, newCat) => {
        const params = {
            search: newSearch !== undefined ? newSearch : search,
            category: (newCat !== undefined ? newCat : activeCategory) === 'all' ? '' : (newCat !== undefined ? newCat : activeCategory)
        };
        loadMechanics(params, 1, true).then(data => {
            setHasMore(1 < (data.pagination?.totalPages || 1));
        });
        setCurrentPage(1);
    }, [search, activeCategory, loadMechanics]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            handleSearch();
        }, 300);
        return () => clearTimeout(timer);
    }, [search, activeCategory]);

    // Load More
    const loadMore = useCallback(async () => {
        if (!loadingMore && hasMore) {
            setLoadingMore(true);
            try {
                const params = { search, category: activeCategory === 'all' ? '' : activeCategory };
                const data = await loadMechanics(params, currentPage + 1);
                setCurrentPage(p => p + 1);
                setHasMore(currentPage + 1 < data.pagination.totalPages);
            } finally {
                setLoadingMore(false);
            }
        }
    }, [loadingMore, hasMore, search, activeCategory, currentPage, loadMechanics]);

    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 200 && hasMore && !loadingMore && !mechanicsLoading) {
            loadMore();
        }
    }, [hasMore, loadingMore, mechanicsLoading, loadMore]);

    return (
        <>
            <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden bg-charcoal">
                {/* Sticky Control Bar */}
                <div className="flex-shrink-0 bg-charcoal-950/80 backdrop-blur-sm border-b border-wine-900/20 px-6 py-3">
                    <div className="max-w-[1800px] mx-auto flex items-center gap-4">
                        <div className="flex-1 relative group">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/40 group-focus-within:text-wine-400 transition-colors" />
                            <input
                                type="text"
                                placeholder="Search mechanisms..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full bg-dark-900 border border-wine-800/50 py-2.5 pl-10 pr-4 text-sm text-cream placeholder-cream/30 focus:outline-none focus:border-wine-500/70"
                            />
                        </div>
                    </div>
                </div>

                {/* Category Filters Bar */}
                <div className="flex-shrink-0 bg-charcoal-900/30 border-b border-wine-900/10 px-6 py-3 overflow-x-auto">
                    <div className="max-w-[1800px] mx-auto flex items-center gap-2">
                        {['all', 'physiological', 'psychological', 'relational', 'environmental'].map(cat => (
                            <button
                                key={cat}
                                onClick={() => setActiveCategory(cat)}
                                className={`px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-all ${activeCategory === cat
                                    ? 'bg-wine-500 text-cream shadow-lg shadow-wine-500/20'
                                    : 'bg-dark-800 text-cream/70 hover:text-cream border border-wine-800/50'
                                    }`}
                            >
                                {cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto w-full" onScroll={handleScroll}>
                    <main className="max-w-[1800px] w-full mx-auto px-6 py-8">
                        {/* Hero Section */}
                        {!search && activeCategory === 'all' && currentPage === 1 && (
                            <DatabaseHero
                                icon={Activity}
                                label="Scientific Inventory"
                                title="Arousal Mechanics"
                                highlightWord="Mechanics"
                                description="The biological and psychological landscape of desire. Explore the mechanisms that drive human arousal."
                                stats={[
                                    { value: totalMechanics, label: 'Mechanisms' },
                                    { value: '4', label: 'Categories' }
                                ]}
                            />
                        )}

                        {/* Content Logic */}
                        {mechanicsError && (
                            <div className="glass-card p-6 text-center text-red-400">
                                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p>{mechanicsError}</p>
                            </div>
                        )}

                        {/* Skeleton Loading State */}
                        {mechanicsLoading && mechanics.length === 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {Array.from({ length: 9 }).map((_, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: i * 0.05 }}
                                        className="bg-charcoal-950/40 border border-wine-900/20 p-6 animate-pulse"
                                    >
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="w-10 h-10 bg-wine-900/20" />
                                            <div className="w-24 h-6 bg-charcoal-900" />
                                        </div>
                                        <div className="h-6 bg-charcoal-900/60 w-3/4 mb-2" />
                                        <div className="space-y-2 mb-6">
                                            <div className="h-3 bg-charcoal-900/40 w-full" />
                                            <div className="h-3 bg-charcoal-900/40 w-5/6" />
                                        </div>
                                        <div className="flex items-center gap-4 pt-4 border-t border-wine-900/10">
                                            <div className="w-16 h-8 bg-charcoal-900/30" />
                                            <div className="w-20 h-8 bg-charcoal-900/30" />
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}

                        {!mechanicsLoading && mechanics.length === 0 && (
                            <div className="bg-charcoal-950/40 p-20 text-center border border-wine-900/10">
                                <Search className="w-12 h-12 mx-auto mb-4 text-wine-900/30" />
                                <h3 className="text-lg font-medium text-cream/60">No mechanisms found</h3>
                                <p className="text-sm text-cream/30">Adjust your search or category filters.</p>
                            </div>
                        )}

                        {mechanics.length > 0 && (
                            <div className="border border-wine-800/40 bg-dark-900/80 overflow-hidden">
                                <div className="sticky top-0 z-10 bg-dark-950 border-b border-wine-800/50 grid grid-cols-[minmax(250px,1fr)_120px_70px_70px_70px_70px_80px] gap-2 py-3 px-4 font-bold text-[10px] uppercase tracking-widest text-cream/60">
                                    <div>Mechanism</div>
                                    <div>Category</div>
                                    <div className="text-center">Evidence</div>
                                    <div className="text-center">Intensity</div>
                                    <div className="text-center">Risk</div>
                                    <div className="text-center">Trainable</div>
                                    <div className="text-center">Dev</div>
                                </div>
                                <div className="divide-y divide-wine-900/10">
                                    {mechanics.map((m, idx) => (
                                        <EditableArousalMechanicRow
                                            key={m.id}
                                            mechanic={m}
                                            rowIndex={idx}
                                            isDevMode={isDevMode}
                                            onUpdate={() => loadMechanics({ search, category: activeCategory === 'all' ? '' : activeCategory }, 1, true)}
                                            onDelete={() => loadMechanics({ search, category: activeCategory === 'all' ? '' : activeCategory }, 1, true)}
                                        />
                                    ))}
                                </div>
                                {loadingMore && (
                                    <div className="p-4 text-center text-wine-500/50 flex items-center justify-center gap-2">
                                        <Spinner size="sm" /> Loading more...
                                    </div>
                                )}
                            </div>
                        )}
                    </main>
                </div>
            </div>
        </>
    );
}

export default ArousalMechanicsPage;
